import { PERMISSIONS } from '@navicosa/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  Descriptions,
  Drawer,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, errorText } from '../../api/client';
import {
  type Customer,
  type CustomerDetail,
  phoneLocal,
  REQUEST_STATUS_COLOR,
  REQUEST_STATUS_LABEL,
} from '../../api/types';
import { useAuth } from '../../auth/AuthContext';

/** Danh sách khách hàng + hồ sơ (drawer). Hồ sơ không bị ghi đè khi khách gửi yêu cầu mới. */
export function CustomersPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { can } = useAuth();
  const canManage = can(PERMISSIONS.CUSTOMERS_MANAGE);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();

  const list = useQuery({
    queryKey: ['customers', q, page],
    queryFn: () => api.page<Customer>('/admin/customers', { q, page, limit: 20 }),
  });
  const detail = useQuery({
    queryKey: ['customer', id],
    queryFn: () => api.get<CustomerDetail>(`/admin/customers/${id}`),
    enabled: !!id,
  });
  const c = detail.data;

  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          Khách hàng
        </Typography.Title>
      }
    >
      <Input.Search
        placeholder="Tên, công ty, SĐT, MST"
        allowClear
        onSearch={(v) => (setQ(v.trim()), setPage(1))}
        style={{ width: 300, marginBottom: 16 }}
      />
      <Table<Customer>
        rowKey="id"
        loading={list.isLoading}
        dataSource={list.data?.items}
        pagination={{
          current: page,
          pageSize: 20,
          total: list.data?.meta.total,
          onChange: setPage,
        }}
        onRow={(r) => ({ onClick: () => nav(`/customers/${r.id}`), style: { cursor: 'pointer' } })}
        columns={[
          {
            title: 'Tên',
            render: (_, r) => (
              <Space direction="vertical" size={0}>
                <span>{r.full_name}</span>
                {r.company_name && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {r.company_name}
                  </Typography.Text>
                )}
              </Space>
            ),
          },
          {
            title: 'SĐT',
            render: (_, r) => (
              <Space>
                {phoneLocal(r.phone)}
                {r.phone_verified && <Tag color="green">Đã xác minh</Tag>}
              </Space>
            ),
          },
          { title: 'Email', dataIndex: 'email', render: (v) => v ?? '—' },
          { title: 'MST', dataIndex: 'tax_code', render: (v) => v ?? '—' },
          { title: 'Yêu cầu', align: 'right', render: (_, r) => r._count?.rental_requests ?? 0 },
          { title: 'Tạo lúc', render: (_, r) => dayjs(r.created_at).format('DD/MM/YYYY') },
          {
            title: 'Trạng thái',
            render: (_, r) =>
              r.status === 'active' ? <Tag color="green">Hoạt động</Tag> : <Tag>Ngưng</Tag>,
          },
        ]}
      />
      <Drawer
        open={!!id}
        width={720}
        title={c ? c.full_name : 'Khách hàng'}
        onClose={() => (setEditing(false), nav('/customers'))}
        extra={
          canManage &&
          c &&
          !editing && (
            <Button
              onClick={() => (
                form.setFieldsValue({ ...c, phone: phoneLocal(c.phone) }),
                setEditing(true)
              )}
            >
              Sửa
            </Button>
          )
        }
        loading={detail.isLoading}
      >
        {c && !editing && (
          <>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Loại">
                {c.customer_type === 'company' ? 'Doanh nghiệp' : 'Cá nhân'}
              </Descriptions.Item>
              <Descriptions.Item label="Công ty">{c.company_name ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="SĐT">{phoneLocal(c.phone)}</Descriptions.Item>
              <Descriptions.Item label="Email">{c.email ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="MST">{c.tax_code ?? '—'}</Descriptions.Item>
            </Descriptions>
            <Typography.Title level={5} style={{ marginTop: 16 }}>
              Địa chỉ công trình
            </Typography.Title>
            {c.addresses.length === 0 ? (
              <Typography.Text type="secondary">Chưa có</Typography.Text>
            ) : (
              <ul>
                {c.addresses.map((a) => (
                  <li key={a.id}>
                    {a.label ? `${a.label}: ` : ''}
                    {a.address}
                    {a.location ? ` (${a.location.name})` : ''}
                    {a.is_default && <Tag style={{ marginLeft: 6 }}>Mặc định</Tag>}
                  </li>
                ))}
              </ul>
            )}
            <Typography.Title level={5} style={{ marginTop: 16 }}>
              Yêu cầu thuê
            </Typography.Title>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={c.rental_requests}
              columns={[
                {
                  title: 'Mã',
                  render: (_, r) => <Link to={`/requests/${r.id}`}>{r.request_code}</Link>,
                },
                { title: 'Ngày gửi', render: (_, r) => dayjs(r.created_at).format('DD/MM/YYYY') },
                {
                  title: 'Bắt đầu thuê',
                  render: (_, r) => dayjs(r.rental_start_at).format('DD/MM/YYYY'),
                },
                { title: 'Phụ trách', render: (_, r) => r.assigned_user?.full_name ?? '—' },
                {
                  title: 'Trạng thái',
                  render: (_, r) => (
                    <Tag color={REQUEST_STATUS_COLOR[r.status]}>
                      {REQUEST_STATUS_LABEL[r.status]}
                    </Tag>
                  ),
                },
              ]}
            />
          </>
        )}
        {c && editing && (
          <Form
            form={form}
            layout="vertical"
            onFinish={async (v) => {
              try {
                await api.patch(`/admin/customers/${c.id}`, {
                  ...v,
                  company_name: v.company_name || null,
                  email: v.email || null,
                  tax_code: v.tax_code || null,
                });
                message.success('Đã lưu');
                setEditing(false);
                void qc.invalidateQueries({ queryKey: ['customer', id] });
                void qc.invalidateQueries({ queryKey: ['customers'] });
              } catch (e) {
                message.error(errorText(e));
              }
            }}
          >
            <Form.Item name="customer_type" label="Loại">
              <Select
                options={[
                  { value: 'individual', label: 'Cá nhân' },
                  { value: 'company', label: 'Doanh nghiệp' },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="full_name"
              label="Tên người liên hệ"
              rules={[{ required: true, min: 2 }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="company_name" label="Công ty">
              <Input />
            </Form.Item>
            <Form.Item name="phone" label="SĐT" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="email" label="Email">
              <Input />
            </Form.Item>
            <Form.Item name="tax_code" label="MST">
              <Input />
            </Form.Item>
            <Form.Item name="status" label="Trạng thái">
              <Select
                options={[
                  { value: 'active', label: 'Hoạt động' },
                  { value: 'inactive', label: 'Ngưng' },
                ]}
              />
            </Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Lưu
              </Button>
              <Button onClick={() => setEditing(false)}>Hủy</Button>
            </Space>
          </Form>
        )}
      </Drawer>
    </Card>
  );
}
